import type { ReactNode } from 'react';
import { AlertCircle, FileQuestion, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Wiederverwendbare Data-State-Komponenten für Listen, Detail-Seiten und
 * Dashboard-Widgets. Konsistente UX bei:
 *   - lädt gerade  → `<LoadingState />`
 *   - kein Ergebnis → `<EmptyState />`
 *   - Fehler       → `<ErrorState />`
 *
 * Vorher hat jede Page eigene "Wird geladen …"-Zeilen und ad-hoc "—"-Texte
 * gerendert. Diese Komponenten vereinheitlichen das.
 */

export function LoadingState({
  label = 'Wird geladen …',
  size = 'default',
}: {
  label?: string;
  size?: 'sm' | 'default';
}) {
  const h = size === 'sm' ? 'h-4 w-4' : 'h-5 w-5';
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-center justify-center gap-2 p-6 text-sm text-ink-muted"
    >
      <Loader2 className={`${h} animate-spin`} />
      <span>{label}</span>
    </div>
  );
}

export function EmptyState({
  title,
  description,
  icon: Icon = FileQuestion,
  action,
}: {
  title: string;
  description?: string;
  icon?: React.ComponentType<{ className?: string }>;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 p-8 text-center">
      <Icon className="h-10 w-10 text-ink-muted" aria-hidden="true" />
      <div>
        <h3 className="text-base font-medium text-ink">{title}</h3>
        {description && <p className="mt-1 text-sm text-ink-muted">{description}</p>}
      </div>
      {action}
    </div>
  );
}

export function ErrorState({
  title = 'Fehler beim Laden',
  message,
  onRetry,
}: {
  title?: string;
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div
      role="alert"
      className="flex flex-col items-start gap-3 border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive"
    >
      <div className="flex items-center gap-2">
        <AlertCircle className="h-4 w-4" aria-hidden="true" />
        <strong>{title}</strong>
      </div>
      <p className="text-ink-soft">{message}</p>
      {onRetry && (
        <Button size="sm" variant="outline" onClick={onRetry}>
          <RefreshCw className="h-3.5 w-3.5" />
          Erneut versuchen
        </Button>
      )}
    </div>
  );
}
